import {
    Card,
    Colors,
    FOCUS_RING,
    Heading,
    Icon,
    Label,
    RadioGroup,
    Text,
    TextInput,
    type IconProps,
    type RadioGroupOption,
} from '@amelio/ui-library';
import { useCallback, useMemo } from 'react';

import {
    COMPENSATION_AUDIENCE_CRITERION_PLACEHOLDER,
    COMPENSATION_CAPTION,
    COMPENSATION_SECTION_TITLE,
} from '../compensationStyles';
import { AudienceCriteriaPicker } from './AudienceCriteriaPicker';
import { CompensationLoadFailedState } from './CompensationLoadFailedState';
import {
    fromCompensationAudienceFilters,
    isMissingAudienceCriterion,
    toCompensationAudienceFilters,
} from '../utils/audienceRequest';

import type { CompensationAudience, CompensationComputedView } from '@/types/compensation';
import type { Company, CompanyAttribute } from '@/types/directory/Company';

import { AudienceTile } from '@/components/AudienceTile/AudienceTile';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import {
    defaultFiltersState,
    type SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import { getCompanyAttributeList } from '@/utils/company';

const ENVELOPE_NAME_INPUT_ID = 'compensation-envelope-name';

/** Groups the two radios; also the stem of each tile's test id (`…-option-AllCompany`). */
const AUDIENCE_RADIO_NAME = 'compensation-audience';

/** The same two glyphs the cycle-evaluation audience step uses for the same two choices. */
const AUDIENCE_ICONS: Record<CompensationAudience['type'], IconProps['name']> = {
    AllCompany: 'building-building-4',
    SpecificTeams: 'users-people',
};

/** What a locked picker reports: nothing, since nobody can change a card it shows. */
const IGNORE_FILTERS = () => undefined;

const hasSavedValue = (selection: SelectedFilters | undefined) =>
    !!selection && Object.values(selection).some((values) => values.length > 0);

interface AudienceCriteriaProps {
    audienceType: CompensationAudience['type'];
    /** Whether the reader may change the criteria; a reader who may not sees the same cards, locked. */
    canEdit: boolean;
    company: Company | undefined;
    /** The company's attributes could not be loaded, so there is nothing to build a criterion from. */
    hasCompanyFailed: boolean;
    onRetryCompany: () => void;
    /** The criteria the cards open with; `undefined` when there are none. */
    savedSelection: SelectedFilters | undefined;
    onFiltersChange: (filters: SelectedFilters) => void;
}

/** What sits under the two audience tiles: nothing for "Toute l'entreprise", otherwise the criteria
 * cards — live for someone who may edit, locked for someone who may not. A locked reader is never
 * shown an empty card asking them to choose: the status line below says no criterion was chosen. */
const AudienceCriteria = ({
    audienceType,
    canEdit,
    company,
    hasCompanyFailed,
    onRetryCompany,
    savedSelection,
    onFiltersChange,
}: AudienceCriteriaProps) => {
    if (audienceType === 'AllCompany' || (!canEdit && !hasSavedValue(savedSelection))) {
        return null;
    }

    // The cards read their seed once, on mount: they wait for the company's attributes so a saved
    // audience never opens on empty cards that the directory would have filled.
    if (company) {
        return (
            <AudienceCriteriaPicker
                company={company}
                defaultFilters={savedSelection}
                isReadOnly={!canEdit}
                onFiltersChange={canEdit ? onFiltersChange : IGNORE_FILTERS}
            />
        );
    }

    return hasCompanyFailed ? (
        <CompensationLoadFailedState onRetry={onRetryCompany} />
    ) : (
        <Skeleton>
            <Bone className={COMPENSATION_AUDIENCE_CRITERION_PLACEHOLDER} />
        </Skeleton>
    );
};

interface AudienceSectionProps {
    name: string;
    audience: CompensationAudience;
    /** Whether the envelope may reach the whole company, which names the first tile. */
    isCompanyWide: boolean;
    computed: CompensationComputedView | undefined;
    company: Company | undefined;
    hasCompanyFailed: boolean;
    onRetryCompany: () => void;
    /** Whether the envelope is still open: its settings can be changed and it can be deleted. */
    canEdit: boolean;
    /** Set when a save came back with a duplicate-name conflict, so the message lands on the field
     * that caused it rather than in a toast the reader has to map back to a control. */
    nameError: string | undefined;
    onNameChange: (name: string) => void;
    onAudienceChange: (audience: CompensationAudience) => void;
    onDelete: () => void;
}

/**
 * Section 1: who the envelope covers. The envelope's name, the two audience tiles, the attribute
 * filters behind "Équipes spécifiques", and how many people that adds up to.
 */
export const AudienceSection = ({
    name,
    audience,
    isCompanyWide,
    computed,
    company,
    hasCompanyFailed,
    onRetryCompany,
    canEdit,
    nameError,
    onNameChange,
    onAudienceChange,
    onDelete,
}: AudienceSectionProps) => {
    const { t } = useAmelioTranslations();

    const handleFiltersChange = useCallback(
        (filters: SelectedFilters) =>
            onAudienceChange({
                type: 'SpecificTeams',
                filters: toCompensationAudienceFilters(filters),
            }),
        [onAudienceChange],
    );

    // The audience read back into the shape the cards open with, so reopening a "Équipes
    // spécifiques" envelope shows the chips that define it. The cards only read it on mount.
    const savedSelection = useMemo<SelectedFilters | undefined>(() => {
        if (audience.type !== 'SpecificTeams' || audience.filters.length === 0) {
            return undefined;
        }

        const idsByFamily = fromCompensationAudienceFilters(audience.filters);
        const attributes = getCompanyAttributeList(company);
        const selected = (family: keyof typeof idsByFamily): CompanyAttribute[] =>
            ((attributes[family]?.values ?? []) as CompanyAttribute[]).filter(
                (value) => !!value.id && idsByFamily[family].includes(value.id),
            );

        return {
            ...defaultFiltersState,
            departments: selected('departments'),
            jobTitles: selected('jobTitles'),
            companyAddresses: selected('companyAddresses'),
        };
    }, [audience, company]);

    const audienceOptions: RadioGroupOption[] = [
        {
            label: t(
                isCompanyWide ? 'compensation.audience.allCompany' : 'compensation.audience.all',
            ),
            value: 'AllCompany',
        },
        { label: t('compensation.audience.specific'), value: 'SpecificTeams' },
    ];

    // Switching back to the whole audience drops the filters; switching to "Équipes spécifiques"
    // keeps whatever was saved, so a reader who taps the wrong tile does not lose their criteria.
    const handleAudienceTypeChange = (value: string) =>
        onAudienceChange(
            value === 'AllCompany'
                ? { type: 'AllCompany', filters: [] }
                : { type: 'SpecificTeams', filters: audience.filters },
        );

    const audienceCount = computed?.lines.length ?? 0;
    const hasOverlap = (computed?.overlappingEmployeeIds.length ?? 0) > 0;
    const isMissingCriterion = isMissingAudienceCriterion(audience);

    return (
        <Card
            glass={'lg'}
            borderRadius={'3xl'}
            dataTestId={'compensation-audience-section'}
        >
            <div className={'flex flex-col gap-[16px]'}>
                <div className={'flex items-start justify-between gap-3'}>
                    <Heading
                        level={2}
                        color={Colors.BASE_500}
                        className={COMPENSATION_SECTION_TITLE}
                    >
                        {t('compensation.audience.title')}
                    </Heading>

                    {canEdit ? (
                        <button
                            type={'button'}
                            onClick={onDelete}
                            aria-label={t('compensation.audience.deleteEnvelope')}
                            data-testid={'compensation-delete-envelope'}
                            /* Turns red on hover: deleting an envelope takes its saved increases
                               with it, and the one control that does that should not look like
                               the neighbouring neutral actions until the pointer is on it. */
                            className={`${FOCUS_RING} text-base-400 hover:bg-error-50 hover:text-error-800 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[8px] transition-colors`}
                        >
                            <Icon
                                name={'essential-trash'}
                                size={'sm'}
                                color={'inherit'}
                                ariaHidden
                            />
                        </button>
                    ) : null}
                </div>

                <div className={'flex max-w-md flex-col gap-1'}>
                    <Label
                        htmlFor={ENVELOPE_NAME_INPUT_ID}
                        color={Colors.BASE_500}
                    >
                        {t('compensation.audience.nameLabel')}
                    </Label>

                    {/* The envelope's name is a value, not a hint: drawn in the library's default
                        input grey it read as placeholder text next to the navy budget figure
                        beneath it, and an envelope called "Enveloppe 1" looked unnamed. */}
                    <TextInput
                        id={ENVELOPE_NAME_INPUT_ID}
                        className={'text-navy font-semibold'}
                        value={name}
                        onChange={onNameChange}
                        readOnly={!canEdit}
                        error={nameError !== undefined}
                        errorMessage={nameError}
                        placeholder={t('compensation.audience.namePlaceholder')}
                        dataTestId={'compensation-envelope-name-input'}
                    />
                </div>

                <RadioGroup
                    className={'w-full max-w-[768px] [&>div]:gap-[12px]'}
                    columns={2}
                    name={AUDIENCE_RADIO_NAME}
                    dataTestId={AUDIENCE_RADIO_NAME}
                    options={audienceOptions}
                    value={audience.type}
                    readOnly={!canEdit}
                    renderOption={(optionProps) => (
                        <AudienceTile
                            {...optionProps}
                            description={
                                optionProps.option.value === 'AllCompany'
                                    ? t(
                                          isCompanyWide
                                              ? 'compensation.audience.allCompanyDescription'
                                              : 'compensation.audience.allDescription',
                                      )
                                    : t('compensation.audience.specificDescription')
                            }
                            iconName={
                                AUDIENCE_ICONS[
                                    optionProps.option.value as CompensationAudience['type']
                                ]
                            }
                        />
                    )}
                    onChange={handleAudienceTypeChange}
                />

                <AudienceCriteria
                    audienceType={audience.type}
                    canEdit={canEdit}
                    company={company}
                    hasCompanyFailed={hasCompanyFailed}
                    onRetryCompany={onRetryCompany}
                    savedSelection={savedSelection}
                    onFiltersChange={handleFiltersChange}
                />

                <div className={'flex flex-col gap-1'}>
                    <Text color={Colors.NAVY}>
                        {t('compensation.audience.includedCount', { count: audienceCount })}
                    </Text>

                    {/* Zero people is the answer, not a glitch: say which choice produced it. */}
                    {/* The live region stays mounted so the sentence is announced when it appears. */}
                    <div
                        role={'status'}
                        data-testid={'compensation-audience-no-criterion'}
                    >
                        {isMissingCriterion ? (
                            <Text
                                color={Colors.BASE_500}
                                className={COMPENSATION_CAPTION}
                            >
                                {t(
                                    canEdit
                                        ? 'compensation.audience.noCriterion'
                                        : 'compensation.audience.noCriterionReadOnly',
                                )}
                            </Text>
                        ) : null}
                    </div>
                </div>

                {hasOverlap ? (
                    <div
                        className={'bg-warning-50 flex items-start gap-2 rounded-[12px] p-[12px]'}
                        data-testid={'compensation-audience-overlap'}
                    >
                        <Icon
                            name={'essential-warning-2'}
                            size={'sm'}
                            color={Colors.WARNING}
                            className={'mt-0.5 flex-shrink-0'}
                            ariaHidden
                        />

                        <Text color={Colors.NAVY}>{t('compensation.audience.overlapWarning')}</Text>
                    </div>
                ) : null}
            </div>
        </Card>
    );
};
